import React from 'react';
import { Container, Typography, Box, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEO/SEOHead';
import PageHeader from '../components/Layout/PageHeader';

const buildTermsSchema = (lng) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Terminos de Uso - FinMaple',
  description: 'Terminos y condiciones de uso del sitio web FinMaple. Contenido educativo sobre finanzas personales en Canada.',
  url: 'https://www.finmaple.com/terminos-de-uso',
  publisher: {
    '@type': 'Organization',
    name: 'FinMaple',
    url: 'https://www.finmaple.com',
  },
  inLanguage: lng,
  availableLanguage: ['es', 'en', 'fr'],
});

const TerminosDeUsoPage = () => {
  const { i18n } = useTranslation();
  return (
    <Box sx={{ bgcolor: '#F7F9FC', minHeight: '100vh' }}>
      <SEOHead
        seoKey="terms"
        title="Terminos de Uso"
        description="Terminos y condiciones de uso de FinMaple. Conoce las reglas de uso de nuestro sitio de educacion financiera para latinos en Canada."
        canonical="/terminos-de-uso"
        schema={buildTermsSchema(i18n.language)}
        routeKey="terms"
        noIndex={i18n.language !== 'es'}
      />

      <PageHeader
        variant="functional"
        title="Terminos de Uso"
        subtitle="Condiciones que rigen el uso de FinMaple."
        breadcrumbs={[{ label: 'Terminos de Uso' }]}
      />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          sx={{
            bgcolor: '#fff',
            borderRadius: '14px',
            p: { xs: 3, md: 5 },
            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          }}
        >
          {/* Intro */}
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Al acceder y utilizar el sitio web finmaple.com ("FinMaple", "el sitio", "nosotros"), aceptas estos terminos de uso. Si no estas de acuerdo con alguno de ellos, te pedimos que no utilices el sitio. Nos reservamos el derecho de modificar estos terminos en cualquier momento; los cambios entraran en vigor al ser publicados en esta pagina.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 1: Nature of service */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            1. Naturaleza del servicio
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            FinMaple es una plataforma de <strong>educacion financiera y comparacion de productos</strong> dirigida a inmigrantes latinos en Canada. Todo el contenido publicado en el sitio - incluyendo articulos, guias, comparadores, herramientas y respuestas de nuestro asistente Maya - tiene fines <strong>exclusivamente informativos y educativos</strong>.
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            FinMaple <strong>no es una institucion financiera</strong>, no esta regulado como asesor financiero y no ofrece servicios de asesoria financiera, fiscal, legal ni contable. No procesamos transacciones, no otorgamos creditos ni gestionamos inversiones.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 2: User responsibilities */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            2. Responsabilidades del usuario
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Al usar FinMaple, te comprometes a:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, color: '#1A1A1A' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Usar el sitio y su contenido unicamente para fines personales y no comerciales.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                No reproducir, distribuir ni modificar el contenido de FinMaple sin autorizacion previa por escrito.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                No intentar acceder de forma no autorizada a los sistemas o infraestructura del sitio.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Verificar la informacion con fuentes oficiales (CRA, bancos, asesores certificados) antes de tomar decisiones financieras basadas en nuestro contenido.
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 3: Affiliate relationships */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            3. Relaciones de afiliados
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            FinMaple genera ingresos a traves de <strong>enlaces de afiliados</strong>. Cuando haces clic en un enlace a un producto financiero (tarjetas de credito, cuentas bancarias, servicios de remesas, entre otros) y completas una solicitud o registro, es posible que recibamos una comision del proveedor. Nuestros socios incluyen empresas como Borrowell, Neo Financial, Tangerine y Scotiabank, entre otros.
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Esta relacion comercial:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, color: '#1A1A1A' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>No tiene costo adicional para ti.</strong> El precio, las tasas y las condiciones del producto son los mismos que obtendrias directamente con el proveedor.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>No influye en nuestras recomendaciones editoriales.</strong> Evaluamos todos los productos con los mismos criterios objetivos, independientemente de si generan comision.
              </Typography>
            </li>
          </Box>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Para mas detalles sobre nuestro modelo de negocio, visita nuestra pagina de <a href={`/${i18n.language}/como-monetizamos`} style={{ color: '#1E3A5F' }}>como monetizamos</a>.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 4: Intellectual property */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            4. Propiedad intelectual
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Todo el contenido publicado en FinMaple - incluyendo textos, imagenes, graficos, logotipos, iconos, herramientas interactivas y el diseno general del sitio - es propiedad de FinMaple o se utiliza con la autorizacion correspondiente, y esta protegido por las leyes de propiedad intelectual de Canada.
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Se permite compartir enlaces a nuestro contenido y citar fragmentos breves con atribucion a FinMaple (finmaple.com). La reproduccion total o parcial del contenido sin autorizacion escrita esta prohibida.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 5: Disclaimer (YMYL) */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            5. Aviso importante (YMYL)
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            FinMaple cubre temas que pueden afectar directamente tu situacion financiera y tu bienestar economico. En la industria, este tipo de contenido se conoce como <strong>YMYL (Your Money, Your Life)</strong>.
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Por esta razon, es fundamental que entiendas lo siguiente:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, color: '#1A1A1A' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                El contenido de FinMaple es <strong>informativo y educativo</strong>, no constituye asesoria financiera, fiscal, legal ni contable.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Las situaciones financieras son individuales. Lo que funciona para una persona puede no ser adecuado para otra.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Siempre consulta a un profesional calificado</strong> (asesor financiero certificado, contador, abogado) antes de tomar decisiones financieras importantes.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Los productos financieros, tasas de interes, leyes fiscales y regulaciones pueden cambiar sin previo aviso. Verificamos nuestro contenido regularmente, pero no podemos garantizar que toda la informacion este actualizada en todo momento.
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 6: Limitation of liability */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            6. Limitacion de responsabilidad
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            FinMaple proporciona el sitio y su contenido "tal cual" y "segun disponibilidad". No garantizamos que el sitio este libre de errores, interrupciones o que la informacion sea completa o exacta en todo momento.
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            En la maxima medida permitida por la ley, FinMaple y sus fundadores, editores y colaboradores no seran responsables por:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2, color: '#1A1A1A' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Perdidas financieras o danos de cualquier tipo derivados del uso de la informacion publicada en el sitio.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Decisiones tomadas basandose en el contenido de FinMaple sin consultar a un profesional.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                El contenido, las practicas de privacidad o la disponibilidad de sitios web de terceros enlazados desde FinMaple.
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                Interrupciones temporales del servicio o errores tecnicos.
              </Typography>
            </li>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 7: Governing law */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            7. Ley aplicable
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Estos terminos de uso se rigen por las leyes de Canada y la provincia donde opera FinMaple. Cualquier disputa relacionada con el uso de este sitio sera resuelta bajo la jurisdiccion de los tribunales canadienses competentes.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 8: Changes */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            8. Cambios a estos terminos
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Podemos actualizar estos terminos de uso ocasionalmente. Los cambios seran publicados en esta pagina con la fecha de actualizacion correspondiente. El uso continuado del sitio despues de la publicacion de cambios constituye tu aceptacion de los terminos actualizados.
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Section 9: Contact */}
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 3 }}>
            9. Contacto
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            Si tienes preguntas sobre estos terminos de uso, puedes contactarnos en:
          </Typography>
          <Typography variant="body1" sx={{ color: '#1A1A1A', lineHeight: 1.8, mb: 2 }}>
            <strong>Correo electronico:</strong> hola@finmaple.com
          </Typography>

          <Divider sx={{ my: 4 }} />

          {/* Last updated */}
          <Typography variant="body2" sx={{ color: '#5F6B7A', fontStyle: 'italic' }}>
            Ultima actualizacion: Febrero 2026
          </Typography>
        </Box>

        {/* Disclaimer */}
        <Divider sx={{ my: 3 }} />
        <Box sx={{ py: 2 }}>
          <Typography variant="body2" sx={{ color: '#5F6B7A', lineHeight: 1.6, fontStyle: 'italic' }}>
            La informacion en FinMaple es educativa y no constituye asesoramiento financiero profesional. Consulta a un asesor financiero certificado antes de tomar decisiones de inversion o credito.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TerminosDeUsoPage;
